/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { HeroAlphaMainStackFragment } from "@cosmos/hygraph-schema";

import { CosmosButtonProps, CosmosTextVariantEnum, LogoTitleDescButtonStack } from "@components/elements";
import { useIsFirefox } from "@components/hooks";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

const blurred = css({
	zIndex: "2",

	minHeight: "550px",
	lgDown: {
		minHeight: "initial",
	},

	"&.ff": {
		_after: {
			height: "500px",
			width: "500px",
			borderRadius: "500px",
			filter: "blur(300px) opacity(0.6)",
		},
	},

	"& .logo img": {
		lgDown: {
			display: "none",
		},
	},

	_after: {
		content: "''",
		animation: `fadein 2s ease-out 1s`,
		animationFillMode: "forwards",
		position: "absolute",
		zIndex: 1,
		opacity: 0,
		height: "400px",
		width: "400px",
		borderRadius: "400px",
		background: "#000",
		filter: "blur(140px)",

		lgDown: {
			content: "initial",
		},
	},
});

const styles = css({
	h: "full",
	maxWidth: "600px",

	"& .logo img": {
		maxHeight: "32px",
		smDown: {
			maxHeight: "24px",
		},
	},
	"& .cosmos-text.cosmos-title": {
		smDown: {
			textStyle: "headingXL",
		},
	},

	"& .cosmos-text.cosmos-desc": {
		lgDown: {
			textStyle: "paragraphMD",
		},
	},

	"& .logo-title-desc-button-stack": {
		position: "relative",
		zIndex: 2,
	},

	"& .button-list": {
		smDown: {
			flexWrap: "wrap",
			alignItems: "flex-start",
		},
	},
});

export interface HeroAlphaMainStackProps extends HeroAlphaMainStackFragment {
	isBlurred?: boolean;
}

export const HeroAlphaMainStack = ({
	isBlurred,
	buttons = [],
	modButtons = [],
	title,
	desc,
	logoImage,
	eyebrow,
	gradient,
}: HeroAlphaMainStackProps): React.JSX.Element | null => {
	//ff doesn't support blur the same as chrome, we need style tweaks
	const isFirefox = useIsFirefox();

	if (!title?.text && !desc && !buttons.length) {
		return null;
	}

	const buttonSource = modButtons?.length ? modButtons : buttons;

	const cosmosButtons = buttonSource.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component: "HeroAlpha",
					section: "hero",
				},
			},
		};
	});

	return (
		<VStack
			justifyContent="center"
			height="100%"
			alignItems="start"
			className={cx(`hero-alpha-main-stack`, isFirefox ? "ff" : "", isBlurred ? blurred : null, styles)}
		>
			<LogoTitleDescButtonStack
				animate={true}
				stack={{ gap: isBlurred ? "eds.dimension.24" : "eds.dimension.32" }}
				image={logoImage}
				eyebrow={eyebrow}
				titleDescStack={{
					stack: { gap: "eds.dimension.24", align: "start" },
					title: {
						...title,
						as: "h1",
						variant: CosmosTextVariantEnum.heading2XL,
						color: "eds.text.contrast",
					},
					gradient,
					description: {
						html: desc,
						variant: CosmosTextVariantEnum.paragraphMD,
						as: "div",
						color: "eds.text.contrast",
					},
				}}
				buttons={cosmosButtons}
			/>
		</VStack>
	);
};
